import { Handle } from './handle';
import { Remove } from './remove';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Transform } from '@dnd-kit/utilities';
import type { DraggableSyntheticListeners, UniqueIdentifier } from '@dnd-kit/core';
import { Rect, Section } from '../../types';
import { useEditorStore } from '../../editor-store';
import { shallow } from 'zustand/shallow';
import { HiddenRegularIcon } from 'src/monet/icons';
import { ContentEditableInput } from '../content-editable-input';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import debounce from 'lodash/debounce';
import { isCmdZ } from '../../utils/keyboard';
import { useSortable } from '@dnd-kit/sortable';

export interface Props {
  dragOverlay?: boolean;
  disabled?: boolean;
  dragging?: boolean;
  handle?: boolean;
  handleProps?: any;
  height?: number;
  index?: number;
  fadeIn?: boolean;
  transform?: Transform | null;
  listeners?: DraggableSyntheticListeners;
  sorting?: boolean;
  style?: React.CSSProperties;
  transition?: string | null;
  rect: Rect;
  wrapperStyle?: React.CSSProperties;
  value: React.ReactNode;
  folderID: UniqueIdentifier;
  current: boolean;
  isFolder?: boolean;
  onNameChange: (name: string) => void;
  onCardClick: () => void;
  onRemove?(): void;
  renderItem?(args: {
    dragOverlay: boolean;
    dragging: boolean;
    sorting: boolean;
    index: number | undefined;
    fadeIn: boolean;
    listeners: DraggableSyntheticListeners;
    ref: React.Ref<HTMLElement>;
    style: React.CSSProperties | undefined;
    transform: Props['transform'];
    transition: Props['transition'];
    value: Props['value'];
  }): React.ReactElement;
  setContextMenuState: (pageX: number, pageY: number, renameAction: () => void) => void;
}

export const Item = React.memo(
  React.forwardRef<HTMLLIElement, Props>(
    (
      {
        dragOverlay,
        dragging,
        disabled,
        fadeIn,
        handle,
        handleProps,
        height,
        index,
        onRemove,
        renderItem,
        sorting,
        style,
        value,
        wrapperStyle,
        rect,
        folderID,
        isFolder = false,
        onCardClick,
        current,
        setContextMenuState,
        onNameChange,
        ...props
      },
      ref
    ) => {
      const layout = useEditorStore((state) => state.layout, shallow);
      const { sections } = layout;
      const setFocusRef = useRef<() => void>(() => {});
      const [data, setData] = useState<Section>({} as Section);

      const title = data?.name || '';

      const [editing, setEditing] = useState(false);
      const [inputValue, setInputValue] = React.useState(title);
      const setScrollToTopRef = React.useRef<() => void>(() => {});

      const effectiveTextValue = editing ? inputValue : title;

      const isHidden = sections.find(({ id }) => id === value)?.hidden;

      useEffect(() => {
        setInputValue(title);
      }, [title]);

      const updateName = useRefCallback(() => {
        if (inputValue !== title) {
          onNameChange(inputValue);
        }
      }, [inputValue, title, onNameChange]);

      const debouncedOnNameChange = useMemo(
        () =>
          debounce(() => {
            return updateName();
          }, 500),
        [onNameChange]
      );

      useEffect(() => {
        if (!dragOverlay) {
          return;
        }

        document.body.style.cursor = 'grabbing';

        return () => {
          document.body.style.cursor = '';
        };
      }, [dragOverlay]);

      useEffect(() => {
        const section = sections.filter((section) => section.id === value)[0];
        setData(section);
      }, [sections, value]);

      const { attributes, listeners, transform, transition } = useSortable({ id: value as UniqueIdentifier });

      return renderItem ? (
        renderItem({
          dragOverlay: Boolean(dragOverlay),
          dragging: Boolean(dragging),
          sorting: Boolean(sorting),
          index,
          fadeIn: Boolean(fadeIn),
          listeners,
          ref,
          style,
          transform,
          transition,
          value,
        })
      ) : (
        <li
          ref={ref}
          style={{ ...style }}
          className={classNames('flex box-border touch-manipulation transition-colors py-1 group/section', {
            'z-50 scale-[1] bg-neue-canvas-fg-5 rounded-xl': dragOverlay,
            'bg-red rounded-xl': dragging,
            // 'bg-neue-canvas-fg-10 rounded-xl': dragging,
          })}
          onClick={onCardClick}
          onContextMenu={(e) => {
            e.preventDefault();
            setContextMenuState(e.pageX, e.pageY, () => {
              setEditing(true);
              setFocusRef.current();
            });
          }}
          {...attributes}
          {...listeners}
        >
          <div
            className={classNames('relative w-full flex items-center pl-1 pr-8 rounded-2xl text-neue-canvas-fg', {
              'bg-gray-300': disabled,
            })}
            data-cypress='draggable-item'
            {...(!handle ? listeners : undefined)}
            {...props}
          >
            <span className={classNames('px-1 transition-opacity opacity-0 group-hover/section:opacity-100 z-[99999]')}>
              {onRemove ? <Remove className={classNames()} onClick={onRemove} /> : null}

              <Handle {...handleProps} {...listeners} className='text-neue-canvas-fg' />
            </span>
            <div
              className={classNames('relative flex justify-center items-center w-4 rounded-2xl mr-2', {
                'border-1 border-slate-800': current,
              })}
            >
              {!isFolder && (
                <span className='neue-section-card-name flex h-full justify-center text-neue-journey-small text-[0.5rem] text-neue-canvas-fg items-center absolute z-neue-context-menu cursor-grab'>
                  ●
                </span>
              )}
              {typeof index !== 'undefined' && isFolder && (
                <span className='neue-section-card-name flex h-full justify-center text-neue-journey-small text-neue-canvas-fg items-center absolute z-neue-context-menu cursor-grab'>
                  {index + 1}
                </span>
              )}
              {isHidden && <HiddenRegularIcon className='w-5 h-5 text-black' />}
            </div>
            <ContentEditableInput
              setFocusRef={setFocusRef}
              setScrollToTopRef={setScrollToTopRef}
              className={classNames(
                'neue-section-card-name opacity-100 flex-1 text-neue-journey-medium-strong transition text-neue-canvas-fg bg-transparent min-w-0 line-clamp-2',
                {
                  'opacity-100': current,
                  'opacity-70 group-hover:opacity-100': !current && effectiveTextValue.length > 0,
                  'w-full ': !editing,
                }
              )}
              placeholder='Untitled'
              value={effectiveTextValue}
              onChange={(value) => {
                setInputValue(value);
                debouncedOnNameChange();
              }}
              onKeyDown={(e) => {
                if (isCmdZ(e.nativeEvent)) {
                  debouncedOnNameChange.cancel();
                  const newTitle = (e.target as HTMLDivElement).innerText || '';
                  if (newTitle !== data.name) {
                    onNameChange(newTitle);
                  }
                }
              }}
              onEditStart={() => setEditing(true)}
              onEditEnd={() => setEditing(false)}
            />
          </div>
        </li>
      );
    }
  )
);
